import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { money, dt } from "@/lib/format";
import { TIPO_COMPROBANTE_LABELS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, QrCode } from "lucide-react";

export default function ComprobantePrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/facturas/publica/${id}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "No se encontró este comprobante."));
  }, [id]);

  if (error) return <div className="p-10 text-center text-muted-foreground" data-testid="comprobante-error">{error}</div>;
  if (!data) return <div className="p-10 text-center text-muted-foreground">Cargando…</div>;

  const { factura, pedido, emisor, es_demo } = data;

  return (
    <div className="max-w-md mx-auto p-6" data-testid="comprobante-page">
      <div className="no-print mb-4 flex justify-end">
        <Button onClick={() => window.print()} className="rounded-full gap-2" data-testid="comprobante-print-button">
          <Printer className="h-4 w-4" /> Imprimir
        </Button>
      </div>

      {es_demo && (
        <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold text-center py-2" data-testid="comprobante-demo-badge">
          COMPROBANTE DEMO — NO VÁLIDO ANTE LA DGII
        </div>
      )}

      <div className="border border-border rounded-2xl p-5 text-sm space-y-4">
        <div className="text-center">
          <p className="font-head font-bold text-lg">{emisor.nombre_negocio}</p>
          {emisor.razon_social && <p className="text-xs text-muted-foreground">{emisor.razon_social}</p>}
          {emisor.rnc && <p className="text-xs text-muted-foreground">RNC: {emisor.rnc}</p>}
          {emisor.direccion && <p className="text-xs text-muted-foreground">{emisor.direccion}</p>}
        </div>

        <div className="border-t border-dashed border-border pt-3 text-center">
          <p className="text-xs text-muted-foreground">{TIPO_COMPROBANTE_LABELS[factura.tipo_comprobante]}</p>
          <p className="font-mono font-bold text-base mt-1" data-testid="comprobante-ncf">NCF: {factura.ncf}</p>
          <p className="text-xs text-muted-foreground mt-1">{dt(factura.emitida_en)}</p>
        </div>

        {(factura.rnc_cedula || factura.razon_social) && (
          <div className="border-t border-dashed border-border pt-3">
            <p className="text-xs text-muted-foreground">Comprador</p>
            {factura.razon_social && <p>{factura.razon_social}</p>}
            {factura.rnc_cedula && <p className="text-xs text-muted-foreground">RNC/Cédula: {factura.rnc_cedula}</p>}
          </div>
        )}

        {pedido && (
          <div className="border-t border-dashed border-border pt-3 space-y-1">
            <p className="text-xs text-muted-foreground mb-1">Pedido #{pedido.numero}</p>
            {pedido.items.map((it, i) => (
              <div key={i} className="flex justify-between"><span>{it.cantidad}x {it.nombre}</span><span>{money(it.precio_unitario * it.cantidad)}</span></div>
            ))}
          </div>
        )}

        <div className="border-t border-dashed border-border pt-3 space-y-1">
          <div className="flex justify-between"><span>Monto gravado</span><span>{money(factura.monto_gravado)}</span></div>
          <div className="flex justify-between"><span>ITBIS ({factura.tasa_itbis}%)</span><span>{money(factura.monto_itbis)}</span></div>
          <div className="flex justify-between font-head font-bold text-base"><span>Total</span><span className="text-primary">{money(factura.monto_total)}</span></div>
        </div>

        <div className="border-t border-dashed border-border pt-3 flex items-center gap-3">
          <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center shrink-0" data-testid="comprobante-qr">
            <QrCode className="h-8 w-8 text-muted-foreground" strokeWidth={1.2} />
          </div>
          <p className="text-[10px] text-muted-foreground font-mono break-all">{factura.qr_data}</p>
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-4">¡Gracias por tu compra! 💕</p>
    </div>
  );
}
